import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ReactComponent as KebabIcon } from '../assets/kebab.svg';
import styles from './WidgetDownloadMenu.module.scss';

export type WidgetDownloadFormat = 'svg' | 'jpeg';

interface WidgetDownloadMenuProps {
  /** Whether the widget can be exported as vector SVG (chart widgets only). */
  canExportSvg: boolean;
  onDownload: (format: WidgetDownloadFormat) => void;
  /** Class for the trigger button so it matches the surrounding header icon buttons. */
  triggerClassName?: string;
}

const MENU_WIDTH = 176; // keep in sync with .menu min-width (11rem @ 16px)
const MENU_GAP = 4;
const VIEWPORT_MARGIN = 8;

/**
 * Kebab (⋮) button that opens an image-download menu for a dashboard widget.
 *
 * The menu is portaled to document.body so it isn't clipped by the widget card's
 * `overflow: hidden`, and follows the APG menu-button pattern: focus moves into the
 * menu on open, Arrow keys rove between items, Escape closes and restores focus, and
 * the menu dismisses on outside click, scroll, or resize.
 */
export const WidgetDownloadMenu: React.FC<WidgetDownloadMenuProps> = ({
  canExportSvg,
  onDownload,
  triggerClassName,
}) => {
  const { t } = useTranslation();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const items: Array<{ format: WidgetDownloadFormat; label: string }> = [
    ...(canExportSvg ? [{ format: 'svg' as const, label: t('Download as SVG') as string }] : []),
    { format: 'jpeg', label: t('Download as JPEG') as string },
  ];

  const close = useCallback((restoreFocus = false) => {
    setIsOpen(false);
    if (restoreFocus) triggerRef.current?.focus();
  }, []);

  const updatePosition = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;
    const left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(rect.right - MENU_WIDTH, window.innerWidth - MENU_WIDTH - VIEWPORT_MARGIN),
    );
    setPosition({ top: rect.bottom + MENU_GAP, left });
  }, []);

  const open = useCallback(() => {
    updatePosition();
    setIsOpen(true);
  }, [updatePosition]);

  // Position the menu and move focus to the first item once it is open.
  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
    itemRefs.current[0]?.focus();
  }, [isOpen, updatePosition]);

  // Dismiss on outside interaction, scroll, or resize while open.
  useEffect(() => {
    if (!isOpen) return;

    const onPointerDown = (e: MouseEvent) => {
      const target = e.target as Node;
      if (menuRef.current?.contains(target) || triggerRef.current?.contains(target)) return;
      close();
    };
    const onScrollOrResize = () => close();

    document.addEventListener('mousedown', onPointerDown);
    // capture:true so scrolls inside the dashboard grid (not just window) dismiss it.
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [isOpen, close]);

  const focusItem = (index: number) => {
    const count = itemRefs.current.length;
    if (count === 0) return;
    const next = (index + count) % count;
    itemRefs.current[next]?.focus();
  };

  const handleMenuKeyDown = (e: React.KeyboardEvent) => {
    const currentIndex = itemRefs.current.findIndex((el) => el === document.activeElement);
    switch (e.key) {
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'ArrowDown':
        e.preventDefault();
        focusItem(currentIndex + 1);
        break;
      case 'ArrowUp':
        e.preventDefault();
        focusItem(currentIndex - 1);
        break;
      case 'Home':
        e.preventDefault();
        focusItem(0);
        break;
      case 'End':
        e.preventDefault();
        focusItem(itemRefs.current.length - 1);
        break;
      case 'Tab':
        close();
        break;
      default:
        break;
    }
  };

  const handleTriggerKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen && (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ')) {
      e.preventDefault();
      open();
    }
  };

  const handleSelect = (format: WidgetDownloadFormat) => {
    close(true);
    onDownload(format);
  };

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className={triggerClassName}
        onClick={(e) => {
          e.stopPropagation();
          if (isOpen) {
            close();
          } else {
            open();
          }
        }}
        onKeyDown={handleTriggerKeyDown}
        data-tooltip-id="widget-card-tooltip"
        data-tooltip-content={t('Download')}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-label={t('Download widget') as string}
      >
        <KebabIcon />
      </button>
      {isOpen &&
        createPortal(
          <div
            ref={menuRef}
            className={styles.menu}
            role="menu"
            aria-label={t('Download widget') as string}
            style={{ top: position.top, left: position.left, width: MENU_WIDTH }}
            onKeyDown={handleMenuKeyDown}
          >
            {items.map((item, i) => (
              <button
                key={item.format}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitem"
                className={styles.menuItem}
                onClick={(e) => {
                  e.stopPropagation();
                  handleSelect(item.format);
                }}
              >
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </>
  );
};
